/* eslint-disable react/button-has-type */
// components/Button.tsx
import React from 'react';
import { clsx } from 'clsx';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?:
        | 'primary'
        | 'secondary'
        | 'ghost'
        | 'secondaryOutline'
        | 'primaryOutline'
        | 'ghostError'
        | 'errorOutline'; // Added ghostError variant
    size?: 'small' | 'medium' | 'large' | 'circle' | 'smaller';
    isLoading?: boolean;
    type?: 'submit' | 'reset' | 'button' | undefined;
}

export const Button: React.FC<ButtonProps> = ({
    children,
    variant = 'primary',
    size = 'large',
    isLoading = false,
    className,
    disabled,
    type = 'button',
    ...props
}) => {
    // Define variant-based classes
    const variantClasses = {
        primary:
            'bg-primary text-white hover:bg-primary bg-opacity-85  hover:bg-opacity-100',
        secondary:
            'disabled:bg-black dark:disabled:bg-white dark:disabled:bg-opacity-50 dark:disabled:opacity-40 disabled:bg-opacity-30 disabled:text-white bg-black bg-opacity-85 dark:bg-white dark:text-black text-white hover:bg-black dark:hover:bg-white hover:bg-opacity-100',
        secondaryOutline:
            ' disabled:bg-transparent  disabled:text-black disabled:opacity-30 bg-transparent dark:text-white text-black !border !border-solid dark:!border-white !border-black hover:bg-black dark:hover:bg-white hover:bg-opacity-5 dark:hover:bg-opacity-5',
        errorOutline:
            'disabled:!border-none disabled:text-white bg-base-200 dark:text-error text-error !border !border-solid dark:!border-error !border-error  hover:bg-opacity-5 hover:bg-error',
        primaryOutline:
            'disabled:!border-none disabled:text-primary bg-base-200 dark:text-primary text-primary !border !border-solid dark:!border-primary !border-primary hover:bg-base-200 hover:bg-opacity-50',
        ghost: 'bg-transparent disabled:opacity-40 dark:disabled:text-white text-black dark:text-white disabled:text-black hover:bg-primary-light shadow-none disabled:bg-transparent',
        ghostError:
            'bg-transparent text-error hover:bg-error-light shadow-none', // Added ghostError variant
    };

    // Define size-based classes
    const sizeClasses = {
        small: 'px-3 py-1.5 text-md h-9',
        smaller: 'px-3 py-1 text-xs h-6',
        medium: 'px-4 py-2 text-base h-11',
        large: 'px-6 py-3 text-lg',
        circle: 'btn-circle', // DaisyUI circular button class
    };

    return (
        <button
            type={type}
            className={clsx(
                '!focus:outline-none flex items-center btn  outline-primary border-none justify-center rounded-2xl transition-colors duration-200 min-h-1',
                variantClasses[variant],
                sizeClasses[size],
                disabled || isLoading ? '!cursor-not-allowed' : '',
                className
            )}
            disabled={disabled || isLoading}
            {...props}
        >
            {isLoading && (
                <svg
                    className={`animate-spin  ${size === 'circle' ? '' : 'me-2'}
                        ${size === 'smaller' ? 'h-3 w-3' : 'h-5 w-5'}
                        `}
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                    />
                    <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                </svg>
            )}

            {children}
        </button>
    );
};
